import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { useFestivalExperience } from "./FestivalExperienceContext";
import { DiyaIcon, ToranGarland } from "./FestivalOrnaments";

// Festival decorations layered onto the standard homepage hero (feedback:
// "decorative items about the current festival"). The campaign picks motifs
// from this fixed registry — editors never send markup or CSS. Everything is
// aria-hidden and pointer-events-none so it can't block the hero's CTAs or
// the InstaBook widget, and motion only runs when the user allows it.

const MarigoldString = ({ className = "", length = 6 }) => (
  <svg viewBox={`0 0 14 ${length * 13}`} className={className} aria-hidden="true">
    <line x1="7" y1="0" x2="7" y2={length * 13} stroke="#7A4A12" strokeWidth="0.8" />
    {Array.from({ length }).map((_, i) => (
      <g key={i}>
        <circle cx="7" cy={6 + i * 13} r="5.2" fill={i % 2 ? "#EA580C" : "#F59E0B"} />
        <circle cx="7" cy={6 + i * 13} r="2.3" fill={i % 2 ? "#FDBA74" : "#FCD34D"} />
      </g>
    ))}
  </svg>
);

const Lantern = ({ className = "" }) => (
  <svg viewBox="0 0 40 96" className={className} aria-hidden="true">
    <line x1="20" y1="0" x2="20" y2="22" stroke="#7A4A12" strokeWidth="1" />
    <path d="M12 22h16l-2 6H14z" fill="#B45309" />
    <path d="M8 28h24c3 10 3 26 0 36H8c-3-10-3-26 0-36z" fill="#C2410C" />
    <path d="M14 28h12c1.5 10 1.5 26 0 36H14c-1.5-10-1.5-26 0-36z" fill="#FDBA74" opacity="0.85" className="motion-safe:animate-[fx-glow_3s_ease-in-out_infinite]" />
    <path d="M12 64h16l-3 6H15z" fill="#B45309" />
    {[14, 18, 22, 26].map((x) => (
      <line key={x} x1={x} y1="70" x2={x} y2="92" stroke="#F59E0B" strokeWidth="1.2" />
    ))}
  </svg>
);

const Kalash = ({ className = "" }) => (
  <svg viewBox="0 0 96 110" className={className} aria-hidden="true">
    {[-50, -25, 0, 25, 50].map((r) => (
      <path key={r} d="M48 34 C40 22 42 8 48 2 C54 8 56 22 48 34z" fill="#3F7D32" transform={`rotate(${r} 48 34)`} />
    ))}
    <ellipse cx="48" cy="30" rx="13" ry="12" fill="#8B5A2B" />
    <path d="M30 42h36l-4 8H34z" fill="#D97706" />
    <path d="M34 50h28c14 8 20 22 16 36-4 14-18 22-30 22s-26-8-30-22c-4-14 2-28 16-36z" fill="#C8942E" />
    <path d="M22 76h52" stroke="#8A1C23" strokeWidth="4" />
    <circle cx="48" cy="76" r="6" fill="#8A1C23" />
    <path d="M44 62l4-6 4 6-4 6z" fill="#FFF1C1" />
  </svg>
);

const Dandiya = ({ className = "" }) => (
  <svg viewBox="0 0 80 80" className={className} aria-hidden="true">
    <g strokeLinecap="round">
      <line x1="14" y1="66" x2="62" y2="10" stroke="#C8942E" strokeWidth="6" />
      <line x1="66" y1="66" x2="18" y2="10" stroke="#C8942E" strokeWidth="6" />
      {[[30, 47], [38, 38], [46, 29]].map(([x, y]) => <circle key={`a${x}`} cx={x} cy={y} r="2.4" fill="#C62828" />)}
      {[[50, 47], [42, 38], [34, 29]].map(([x, y]) => <circle key={`b${x}`} cx={x} cy={y} r="2.4" fill="#1E4FBF" />)}
    </g>
    <path d="M62 10 q8 6 4 16" stroke="#EA580C" strokeWidth="2" fill="none" />
    <path d="M18 10 q-8 6 -4 16" stroke="#2E8B3A" strokeWidth="2" fill="none" />
  </svg>
);

const Rangoli = ({ className = "" }) => (
  <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
    {[0, 1, 2].map((ring) =>
      Array.from({ length: 12 }).map((_, i) => (
        <ellipse
          key={`${ring}-${i}`}
          cx="100"
          cy={100 - 30 - ring * 22}
          rx={9 - ring * 1.5}
          ry={18 - ring * 3}
          fill={["#C62828", "#F59E0B", "#7C3AED"][ring]}
          opacity="0.9"
          transform={`rotate(${i * 30 + ring * 15} 100 100)`}
        />
      )),
    )}
    <circle cx="100" cy="100" r="22" fill="#EA580C" />
    <circle cx="100" cy="100" r="11" fill="#FCD34D" />
  </svg>
);

const PETALS = Array.from({ length: 12 }).map((_, i) => ({
  left: `${(i * 83) % 100}%`,
  delay: `${(i * 1.7) % 9}s`,
  duration: `${9 + (i % 5) * 1.6}s`,
  color: i % 3 === 0 ? "#EA580C" : i % 3 === 1 ? "#F59E0B" : "#FCD34D",
  size: 8 + (i % 3) * 3,
}));

export const FestivalHeroDecor = () => {
  const { experience } = useFestivalExperience();
  const decorations = experience?.primaryCampaign?.hero?.decorations || [];
  if (!decorations.length) return null;
  const has = (k) => decorations.includes(k);

  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true" data-fx-impression="home.hero.decor">
      {has("rangoli") && (
        <>
          <Rangoli className="absolute -bottom-24 -left-24 h-72 w-72 opacity-[0.16]" />
          <Rangoli className="absolute -right-20 top-1/3 hidden h-56 w-56 opacity-[0.12] lg:block" />
        </>
      )}

      {has("toran") && (
        <div className="absolute inset-x-0 top-0 md:top-12">
          {/* md+: the hero sits ~48px under the fixed navbar (HeroSection md:top-10) */}
          <ToranGarland className="h-6 w-full" />
          <div className="hidden justify-between px-[4%] md:flex">
            {Array.from({ length: 9 }).map((_, i) => (
              <MarigoldString key={i} length={i % 2 ? 3 : 5} className={`${i % 2 ? "h-10" : "h-16"} w-3.5 -mt-1`} />
            ))}
          </div>
        </div>
      )}

      {has("lanterns") && (
        <>
          <Lantern className="absolute left-[1.5%] top-16 hidden h-24 w-10 origin-top motion-safe:animate-[fx-sway_5s_ease-in-out_infinite] xl:block" />
          <Lantern className="absolute right-[1.5%] top-16 hidden h-28 w-11 origin-top motion-safe:animate-[fx-sway_6s_ease-in-out_infinite] xl:block" />
        </>
      )}

      {has("dandiya") && <Dandiya className="absolute right-[3%] top-32 hidden h-14 w-14 opacity-90 motion-safe:animate-[fx-sway_4s_ease-in-out_infinite] lg:block" />}

      {has("kalash") && <Kalash className="absolute bottom-10 right-[2%] hidden h-28 w-24 drop-shadow-lg lg:block" />}

      {has("diyas") && (
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between px-3 md:px-[6%]">
          {Array.from({ length: 9 }).map((_, i) => (
            <DiyaIcon key={i} className={`${i % 2 ? "h-6 w-6" : "h-8 w-8"} ${i > 4 ? "hidden md:block" : ""} drop-shadow-[0_0_10px_rgba(255,176,32,0.65)]`} />
          ))}
        </div>
      )}

      {has("petals") && (
        <div className="absolute inset-0 hidden motion-safe:block">
          {PETALS.map((p, i) => (
            <span
              key={i}
              className="absolute -top-4 block rounded-[60%_0] animate-[fx-fall_linear_infinite]"
              style={{ left: p.left, width: p.size, height: p.size, backgroundColor: p.color, animationDelay: p.delay, animationDuration: p.duration, opacity: 0.75 }}
            />
          ))}
        </div>
      )}

      {experience.primaryCampaign.hero?.artworkUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={experience.primaryCampaign.hero.artworkUrl} alt="" width={128} height={128} className="absolute bottom-12 left-[2%] hidden h-32 w-32 object-contain opacity-90 xl:block" />
      )}
    </div>
  );
};

// Small link chip above the hero headline: the festival's "door" on the
// first screen of the homepage.
export const FestivalHeroBadge = () => {
  const { experience } = useFestivalExperience();
  if (!experience?.primaryCampaign) return null;
  const { primaryCampaign, activeDay, copy } = experience;
  const text = activeDay ? `${primaryCampaign.festivalName} · ${activeDay.label} · ${activeDay.name.split(" · ")[0]}` : copy.eyebrow || primaryCampaign.name;
  return (
    <Link
      href={primaryCampaign.landingHref}
      data-fx-slot="home.hero.badge"
      className="inline-flex max-w-full items-center gap-2 rounded-full bg-gradient-to-r from-[#5e1016] to-[#b4401a] py-1.5 pl-2 pr-3 font-sans text-xs md:text-sm font-semibold text-[#fdf0d5] shadow-[0_6px_20px_-6px_rgba(180,64,26,0.7)] transition-transform hover:-translate-y-0.5"
    >
      <DiyaIcon className="h-5 w-5 shrink-0" />
      <span className="truncate">{text}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-[#f6d77a]" />
    </Link>
  );
};
